/**
 * DI-02 Office.js reader — walks every slide and reads its speaker notes.
 *
 * The PowerPoint JS API exposes no speaker-notes property, so each slide is
 * exported with `slide.exportAsBase64()` (PowerPointApi 1.8) and its notes are
 * read from the package's notesSlide part (see notesFromSlidePackage). This
 * reads the live presentation, including unsaved edits.
 *
 * Fully local: no AI call, so it keeps working when the AI service is offline
 * (spec ST-03). Each slide is read inside its own try/catch so one failure
 * never hides the others (spec G-7): a slide that can't be exported counts as
 * `unreadable` rather than being skipped silently.
 */

import { logOfficeError } from "../../../../../telemetry";
import { base64ToBytes } from "../../../utils/base64";
import { hasPpt } from "../../../utils/officeHost";
import type { SpeakerNotesScan } from "../types";
import { notesTextFromSlidePackage } from "./notesFromSlidePackage";

type ExportableSlide = { exportAsBase64?: () => OfficeExtension.ClientResult<string> };

/** Reads one slide's notes. Returns null when this host can't export slides. */
async function readSlideNotes(context: PowerPoint.RequestContext, slide: PowerPoint.Slide): Promise<string | null> {
  const exportable = slide as unknown as ExportableSlide;
  if (typeof exportable.exportAsBase64 !== "function") return null;

  const exported = exportable.exportAsBase64();
  await context.sync();
  return notesTextFromSlidePackage(base64ToBytes(exported.value));
}

/**
 * Walks the whole deck. Returns null when PowerPoint (or its slide collection)
 * isn't available at all — the caller renders the "can't read" outcome.
 */
export async function readAllSpeakerNotes(): Promise<SpeakerNotesScan | null> {
  if (!hasPpt()) return null;

  try {
    return await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items");
      await context.sync();

      const scan: SpeakerNotesScan = {
        totalSlides: slides.items.length,
        notes: [],
        emptyCount: 0,
        unreadableCount: 0,
      };

      for (let i = 0; i < slides.items.length; i++) {
        try {
          const text = await readSlideNotes(context, slides.items[i]!);
          if (text === null) scan.unreadableCount++;
          else if (text.trim()) scan.notes.push({ slideNumber: i + 1, text });
          else scan.emptyCount++;
        } catch (e) {
          logOfficeError(e, "ppt-list-notes-slide");
          scan.unreadableCount++;
        }
      }
      return scan;
    });
  } catch (e) {
    logOfficeError(e, "ppt-list-notes");
    return null;
  }
}
